import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import Table from '../../../components/Table';

function AssignUnitModal({
  open,
  onClose,
  units = [],
  unitsTotal = 0,
  unitsPage = 1,
  unitsPageSize = 10,
  unitsLoading = false,
  onPageChange,
  onPageSizeChange,
  onAssign,
}) {
  const { t } = useTranslation();
  const [selectedUnitId, setSelectedUnitId] = useState(null);

  // Reset selection when modal opens
  useEffect(() => {
    if (open) {
      setSelectedUnitId(null);
    }
  }, [open]);

  const handleAssign = () => {
    if (selectedUnitId) {
      onAssign(selectedUnitId);
      setSelectedUnitId(null);
    }
  };

  const handleRowClick = (row) => {
    setSelectedUnitId(row.id);
  };

  const columns = [
    { key: 'code', label: t('licenses.assignDialog.unitCode') },
    { key: 'name', label: t('licenses.assignDialog.unitName') },
    { key: 'location', label: t('licenses.assignDialog.unitLocation') },
  ];

  // Custom row renderer to highlight selected row
  const getRowClassName = (row) => {
    return selectedUnitId === row.id ? 'selected-row' : '';
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          color: 'primary.main',
        }}
      >
        <AssignmentIcon />
        {t('licenses.assignDialog.title')}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Table
            id="assign-units-table"
            columns={columns}
            data={units}
            loading={unitsLoading}
            emptyMessage={t('licenses.assignDialog.noUnits')}
            idField="id"
            pagination={true}
            page={unitsPage}
            pageSize={unitsPageSize}
            totalItems={unitsTotal}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            pageSizeOptions={[5, 10, 20]}
            renderActions={(row) => (
              <Button
                variant={selectedUnitId === row.id ? 'contained' : 'outlined'}
                size="small"
                onClick={() => handleRowClick(row)}
                id={`select-unit-${row.id}`}
                name={`select-unit-${row.id}`}
                data-testid={`assign-units-table-button-select-${row.id}`}
              >
                {selectedUnitId === row.id
                  ? t('licenses.assignDialog.selected')
                  : t('licenses.assignDialog.select')}
              </Button>
            )}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button
          onClick={handleAssign}
          variant="contained"
          color="primary"
          startIcon={<AssignmentIcon />}
          disabled={!selectedUnitId}
          id="assignUnit"
          name="assignUnit"
          data-testid="assign-modal-button-assign"
        >
          {t('licenses.assignDialog.assign')}
        </Button>
        <Button
          onClick={onClose}
          variant="outlined"
          startIcon={<CloseIcon />}
          id="cancel"
          name="cancel"
          data-testid="assign-modal-button-cancel"
        >
          {t('licenses.assignDialog.cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AssignUnitModal;
